import {CallInvoiceIncident, CallInvoicesDTO} from '@tj/tj-dmp-lib-api';

type DateValue = string | number | Date | number[] | null;
type GenerationFile = {filePath?: string};
type Generation = {
    rangeDateStart?: DateValue;
    rangeDateEnd?: DateValue;
    generatedAt?: DateValue;
    status?: string;
    affiliationDetails?: {status?: string; pdfFile?: GenerationFile; excelFiles?: GenerationFile[]}[];
};
export type InvoiceListFilter = 'all' | 'incidents' | 'validation' | 'email' | 'automatic' | 'manual' | 'incomplete';
export type InvoiceRecipientFilter = 'all' | 'TENANT_TO_PROVIDER' | 'TENANT_TO_PARTNER';
export type InvoiceTransactionFilter = 'all' | 'command' | 'lead';
export type InvoiceStatusTone = 'neutral' | 'success' | 'warning' | 'danger';

export interface InvoiceListRow {
    id: string;
    name: string;
    description: string;
    direction: string;
    billingDirection: Exclude<InvoiceRecipientFilter, 'all'> | null;
    partners: string[];
    partnerNames: string;
    products: string[];
    transaction: string;
    transactionType: Exclude<InvoiceTransactionFilter, 'all'> | null;
    start: Date | null;
    end: Date | null;
    createdAt: Date | null;
    frequency: string;
    automatic: boolean;
    schedule: string;
    missingFields: string[];
    missingRecipients: boolean;
    generations: number;
    pendingValidation: number;
    pendingEmailCount: number;
    generationErrors: number;
    latest: {start: Date | null; end: Date | null; status: string; tone: InvoiceStatusTone} | null;
    incidents: number;
    searchText: string;
}

/** LocalDateTime can arrive as a Java array; DatePipe needs a Date or ISO string. */
export function invoiceDate(value?: DateValue): Date | null {
    if (value == null || value === '') { return null; }
    let parsed: Date;
    if (Array.isArray(value)) {
        if (value.length < 3 || !value.every(Number.isFinite)) { return null; }
        const [year, month, day, hour = 0, minute = 0, second = 0, nanos = 0] = value;
        parsed = new Date(year, month - 1, day, hour, minute, second, Math.floor(nanos / 1e6));
        if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) { return null; }
    } else if (value instanceof Date) {
        parsed = value;
    } else if (typeof value === 'number') {
        parsed = new Date(value);
    } else if (typeof value === 'string') {
        // Keep calendar dates in local time, including in browsers west of UTC.
        const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
        return day ? invoiceDate(day.slice(1).map(Number)) : validDate(new Date(value));
    } else {
        return null;
    }
    return validDate(parsed);
}

function validDate(value: Date): Date | null { return Number.isNaN(value.getTime()) ? null : value; }
function codes(value: unknown): string[] {
    const values = Array.isArray(value) ? value : typeof value === 'string' ? value.split(/[,;]+/) : [];
    return [...new Set(values.filter(item => typeof item === 'string').map(item => item.trim()).filter(Boolean))];
}
export function invoiceSearchText(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** One outgoing email per affiliate and period, regardless of the ZIP's file count. */
function pendingEmails(generations: Generation[]): number {
    return generations.filter(generation => ['VALIDEE', 'PLANIFIEE', 'ERROR'].includes(generation.status))
        .reduce((total, generation) => total + (generation.affiliationDetails || [])
            .filter(detail => detail && detail.status !== 'SEND'
                && [detail.pdfFile, ...(detail.excelFiles || [])].some(file => !!file?.filePath?.trim())).length, 0);
}

export function invoiceListRow(call: CallInvoicesDTO, incidents: CallInvoiceIncident[], partnerNames: Map<string, string>, t: (key: string, params?: object) => string = key => key): InvoiceListRow {
    const data = call.searchInvoicePrepareData;
    const settings = data as typeof data & {billingFrequency?: string; invoiceGenerationDayOfMonth?: number; invoiceTitle?: string; statementSuffix?: string; amountMode?: string};
    const billingDirection = settings?.billingDirection;
    const source = call as unknown as {dateTimeCreation?: DateValue; generatedCallInvoices?: Generation[]};
    const partners = codes(settings?.partnerCodes);
    const productCodes = codes(settings?.product);
    const labels: Record<string, string> = {flight: t('flights'), hotel: t('hotels'), car: t('cars'), parking: t('parking')};
    const products = productCodes.map(product => labels[product.toLowerCase()] || product);
    const start = invoiceDate(settings?.rangeDateStart);
    const end = invoiceDate(settings?.rangeDateEnd);
    const frequency = t(settings?.billingFrequency === 'T' ? 'quarterly' : 'monthly');
    const automatic = !!settings && settings.automaticGenerationEnabled !== false;
    const day = settings?.invoiceGenerationDayOfMonth ?? 1;
    const missingFields: string[] = [];
    if (!settings?.name?.trim()) { missingFields.push(t('name')); }
    if (!['TENANT_TO_PARTNER', 'TENANT_TO_PROVIDER'].includes(billingDirection)) { missingFields.push(t('direction')); }
    if (!partners.length) { missingFields.push(t('affiliates')); }
    if (!productCodes.length) { missingFields.push(t('products')); }
    if (!start || settings?.rangeDateEnd && (!end || end < start)) { missingFields.push(t('period')); }
    if (!['command', 'lead'].includes(settings?.typeTransaction)) { missingFields.push(t('transaction')); }
    if (settings?.typeTransaction !== 'lead' && !codes(settings?.product).every(product => codes(settings?.statusCommandsByProduct?.[product.toLowerCase()]).length)) { missingFields.push(t('commandStatuses')); }
    if (settings?.typeTransaction !== 'lead' && !settings?.typeDate) { missingFields.push(t('referenceDate')); }
    if (!settings?.invoiceTitle?.trim()) { missingFields.push(t('statementTitle')); }
    if (!settings?.statementSuffix?.trim()) { missingFields.push(t('statementSuffix')); }
    if (settings?.typeTransaction !== 'lead' && !settings?.amountMode) { missingFields.push(t('totalLabel')); }
    if (!(settings?.sendingblueTemplate > 0)) { missingFields.push(t('emailTemplate')); }
    if (automatic && (!Number.isInteger(day) || day < 1 || day > 31)) { missingFields.push(t('generationDay')); }
    const generations = (source.generatedCallInvoices || []).filter(Boolean);
    const latest = [...generations].sort((a, b) =>
        (invoiceDate(b.rangeDateEnd)?.getTime() || 0) - (invoiceDate(a.rangeDateEnd)?.getTime() || 0)
        || (invoiceDate(b.generatedAt)?.getTime() || 0) - (invoiceDate(a.generatedAt)?.getTime() || 0))[0];
    const statuses: Record<string, [string, InvoiceStatusTone]> = {
        NON_VALIDEE: [t('toValidate'), 'warning'], VALIDEE: [t('validated'), 'success'],
        PLANIFIEE: [t('scheduledSend'), 'neutral'], SEND: [t('sent'), 'success'], ERROR: [t('generationError'), 'danger']
    };
    const status: [string, InvoiceStatusTone] = statuses[latest?.status] || [t('unknownStatus'), 'warning'];
    const name = settings?.name?.trim() || t('unnamed');
    const direction = billingDirection === 'TENANT_TO_PARTNER' ? t('toPartner')
        : billingDirection === 'TENANT_TO_PROVIDER' ? t('toProvider') : t('missingDirection');
    const partnerLabels = partners.map(code => partnerNames.get(code) || code).join(', ');
    return {
        id: call.id, name, description: settings?.description?.trim() || '', direction,
        billingDirection: billingDirection === 'TENANT_TO_PROVIDER' || billingDirection === 'TENANT_TO_PARTNER'
            ? billingDirection : null,
        partners, partnerNames: partnerLabels,
        products, transaction: settings?.typeTransaction === 'command' ? t('purchases') : settings?.typeTransaction === 'lead' ? t('leads') : t('missingTransaction'),
        transactionType: settings?.typeTransaction === 'command' || settings?.typeTransaction === 'lead' ? settings.typeTransaction : null,
        start, end, createdAt: invoiceDate(source.dateTimeCreation), frequency, automatic,
        schedule: automatic ? t(settings?.billingFrequency === 'T' ? 'quarterlySchedule' : 'monthlySchedule', {day}) : t('onDemand'),
        missingFields, missingRecipients: automatic && !codes(settings?.alertEmailRecipients).length,
        generations: generations.length, pendingValidation: generations.filter(generation => generation.status === 'NON_VALIDEE').length,
        pendingEmailCount: pendingEmails(generations),
        generationErrors: generations.filter(generation => generation.status === 'ERROR').length,
        latest: latest ? {start: invoiceDate(latest.rangeDateStart), end: invoiceDate(latest.rangeDateEnd), status: status[0], tone: status[1]} : null,
        incidents: incidents.filter(incident => incident.callInvoiceId === call.id && incident.status !== 'RESOLVED').length,
        searchText: invoiceSearchText([name, settings?.description, direction, ...partners, partnerLabels, ...products, ...productCodes,
            t(settings?.typeTransaction === 'command' ? 'purchaseSearch' : 'leads'), frequency].join(' '))
    };
}
